import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { Subscription, Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { map, switchMap, take, filter } from 'rxjs/operators';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DsButtonVariants } from '@levelaccess/design-system';

import { UserService } from '../../../../../../services/user.service';
import { CustomValidators } from '../../../../../../services/helpers/form-custom-validators';
import { InputFieldContext } from '../../../../../../components/common-input/common-input.component';
import { TranslateService } from '../../../../../../translate/translate.service';
import { LoadErrorHandler } from '../../../../../../services/load-error-handler';
import { ErrorMessageService } from '../../../../../../services/error-message.service';
import { IMfaRegistrationQrResponse } from '../../../../../../../../shared/interfaces/user.interface';
import { SharedCommonUtility } from '../../../../../../../../shared/utils/common.utility';

@Component({
  selector: 'app-mfa-registration-modal',
  styleUrls: ['./mfa-registration-modal.component.scss'],
  templateUrl: './mfa-registration-modal.component.html',
  standalone: false,
})
export class MfaRegistrationModalComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly tokenField: string;
  private subscriptions: Subscription;

  readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;

  public registrationQr$: BehaviorSubject<IMfaRegistrationQrResponse>;
  public qrDataURL$: Observable<string>;
  public qrSecret$: Observable<string>;
  public form: UntypedFormGroup;

  @Output()
  public onMfaEnabled: EventEmitter<void>;

  constructor(
    public readonly modal: NgbActiveModal,
    private userService: UserService,
    private translateService: TranslateService,
    private loadErrorHandler: LoadErrorHandler,
    private changeDetectorRef: ChangeDetectorRef,
    private errorMessageService: ErrorMessageService,
  ) {
    this.tokenField = 'token';
    this.subscriptions = new Subscription();

    this.registrationQr$ = new BehaviorSubject<IMfaRegistrationQrResponse>(undefined);

    const fb: UntypedFormBuilder = new UntypedFormBuilder();
    this.form = fb.group({
      [this.tokenField]: fb.control('', [CustomValidators.required, CustomValidators.validateIsEmpty]),
    });

    this.onMfaEnabled = new EventEmitter<void>();
  }

  private onGetQrSuccess(registrationQr: IMfaRegistrationQrResponse): void {
    this.registrationQr$.next(registrationQr);
  }

  private onGetQrError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('mfa_failed_to_get_qr', error, MfaRegistrationModalComponent.name);
  }

  private onSendTokenSuccess(): void {
    this.onMfaEnabled.emit();
    this.modal.close();
  }

  private onSendTokenError(error: HttpErrorResponse): void {
    const messageKey: string | null = this.errorMessageService.getAppErrorResponse(error);
    const message: string =
      messageKey === null ? this.errorMessageService.getGlobalErrorResponse(error) : this.translateService.instant(messageKey);

    this.formToken.setErrors({ server: { message } });
  }

  private buildObservables(): void {
    this.qrDataURL$ = this.registrationQr$.pipe(
      filter((registrationQr: IMfaRegistrationQrResponse): boolean => SharedCommonUtility.notNullish(registrationQr)),
      map((registrationQr: IMfaRegistrationQrResponse): string => registrationQr.dataURL),
    );

    this.qrSecret$ = this.registrationQr$.pipe(
      filter((registrationQr: IMfaRegistrationQrResponse): boolean => SharedCommonUtility.notNullish(registrationQr)),
      map((registrationQr: IMfaRegistrationQrResponse): string => registrationQr.secret),
    );
  }

  get formToken(): AbstractControl {
    return this.form.get(this.tokenField);
  }

  get formContext(): InputFieldContext {
    return {
      field: this.tokenField,
      required: true,
      noLabel: false,
      hiddenLabel: true,
      label: 'mfa_security_code',
      autocomplete: 'off',
      customMessage: this.formToken.errors?.server?.message,
      placeholder: 'mfa_enter_code_placeholder',
    };
  }

  onContinue(): void {
    this.subscriptions.add(
      combineLatest([this.qrSecret$, of(this.form.get(this.tokenField).value)])
        .pipe(
          switchMap(
            ([secret, token]: [string, string]): Observable<void> => this.userService.verifyAndEnableMfa({ secret, token }),
          ),
          take(1),
        )
        .subscribe({
          next: this.onSendTokenSuccess.bind(this),
          error: this.onSendTokenError.bind(this),
        }),
    );
  }

  onCancel(): void {
    this.modal.dismiss();
  }

  ngOnInit(): void {
    this.buildObservables();

    this.subscriptions.add(
      this.userService.getMfaRegistrationQr().subscribe({
        next: this.onGetQrSuccess.bind(this),
        error: this.onGetQrError.bind(this),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }
}
